import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Divider,
  IconButton,
  Tooltip,
  Collapse,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleIcon from "@mui/icons-material/People";
import SecurityIcon from "@mui/icons-material/Security";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import StorefrontIcon from "@mui/icons-material/Storefront";
import SettingsIcon from "@mui/icons-material/Settings";
import LayersIcon from "@mui/icons-material/Layers";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import InventoryIcon from "@mui/icons-material/Inventory";
import CategoryIcon from "@mui/icons-material/Category";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import HistoryIcon from "@mui/icons-material/History";
import BusinessIcon from "@mui/icons-material/Business";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import PermMediaIcon from "@mui/icons-material/PermMedia";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import CampaignIcon from "@mui/icons-material/Campaign";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { usePermissions } from "../contexts/PermissionContext";

import { Permissions } from "../constants/permissions";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import { LocalStorageKeys } from "../constants/localStorageKeys";
import { AppRoutes } from "../constants/appRoutes";

const FULL_DRAWER_WIDTH = 260;
const COLLAPSED_DRAWER_WIDTH = 72;

const generalItems = [
  {
    label: "Platform Overview",
    path: "/",
    icon: <DashboardIcon />,
    permission: [Permissions.DASHBOARD_VIEW, Permissions.DASHBOARD_ANALYTICS],
    logic: "OR",
  },
  {
    label: "Products",
    path: "/products",
    icon: <LayersIcon />,
    permission: Permissions.PRODUCT_READ,
  },
  {
    label: "Inventory Center",
    path: "/inventory",
    icon: <InventoryIcon />,
    permission: Permissions.PRODUCT_READ,
  },
  {
    label: "Categories",
    path: "/categories",
    icon: <CategoryIcon />,
    permission: Permissions.CATEGORY_READ,
  },
  {
    label: "Brands",
    path: "/brands",
    icon: <StorefrontIcon />,
    permission: Permissions.BRAND_READ,
  },
  {
    label: "IPs",
    path: "/ips",
    icon: <CampaignIcon />,
    permission: Permissions.IP_READ,
  },
  {
    label: "BO Users",
    path: "/bo-users",
    icon: <AdminPanelSettingsIcon />,
    permission: Permissions.BO_USER_READ,
  },
  {
    label: "Roles",
    path: "/roles",
    icon: <SecurityIcon />,
    permission: Permissions.ROLE_READ,
  },
  {
    label: "Audit Logs",
    path: "/audit-logs",
    icon: <HistoryIcon />,
    permission: Permissions.AUDIT_LOG_READ,
  },
  {
    label: "Permissions",
    path: "/permissions",
    icon: <VpnKeyIcon />,
    permission: Permissions.PERMISSION_READ,
  },
  {
    label: "Settings",
    path: "/settings",
    icon: <SettingsIcon />,
    permission: Permissions.SYSTEM_SETTINGS_READ,
  },
  {
    label: "Cross Projects",
    path: "/cross-projects",
    icon: <SyncAltIcon />,
    permission: Permissions.CROSS_PROJECT_READ,
  },
  {
    label: "System Health",
    path: "/system-health",
    icon: <HealthAndSafetyIcon />,
    permission: Permissions.SYSTEM_SETTINGS_READ,
  },

  {
    label: "Asset Manager",
    path: "/media-assets",
    icon: <PermMediaIcon />,
    permission: Permissions.CONTENT_READ,
  },
];

const b2cItems = [
  {
    label: "B2C Pulse",
    path: "/b2c/dashboard",
    icon: <DashboardIcon />,
    permission: [Permissions.DASHBOARD_VIEW, Permissions.DASHBOARD_ANALYTICS],
    logic: "OR",
  },
  {
    label: "B2C Users",
    path: "/users",
    icon: <PeopleIcon />,
    permission: Permissions.USER_READ,
  },
  {
    label: "B2C Orders Center",
    path: "/orders",
    icon: <ShoppingCartIcon />,
    permission: Permissions.ORDER_READ,
  },
  {
    label: "Wallets",
    path: "/wallets",
    icon: <AccountBalanceWalletIcon />,
    permission: Permissions.WALLET_READ,
  },
  {
    label: "B2C Transactions",
    path: "/transactions",
    icon: <ReceiptLongIcon />,
    permission: Permissions.TRANSACTION_READ,
  },
  {
    label: "Address Book",
    path: "/addresses",
    icon: <LocationOnIcon />,
    permission: Permissions.USER_READ,
  },
  {
    label: "Coupons",
    path: "/coupons",
    icon: <LocalOfferIcon />,
    permission: Permissions.COUPON_READ,
  },
  {
    label: "Tiers",
    path: "/tiers",
    icon: <LayersIcon />,
    permission: Permissions.TIER_READ,
  },
  {
    label: "Cashback Rules",
    path: "/cashback-rules",
    icon: <MonetizationOnIcon />,
    permission: Permissions.SYSTEM_SETTINGS_READ,
  },
  {
    label: "Redemption Rules",
    path: "/redemption-rules",
    icon: <PointOfSaleIcon />,
    permission: Permissions.SYSTEM_SETTINGS_READ,
  },
  {
    label: "Jigenpass Cards",
    path: "/cards",
    icon: <CreditCardIcon />,
    permission: Permissions.CARD_READ,
  },
];

const supportItems = [
  {
    label: "RMA Center",
    path: "/rma/desk",
    icon: <HealthAndSafetyIcon />,
    permission: Permissions.RMA_READ,
  },
];

const b2bItems = [
  {
    label: "B2B Pulse",
    path: "/b2b/dashboard",
    icon: <DashboardIcon />,
    permission: [
      Permissions.B2B_DASHBOARD_VIEW,
      Permissions.B2B_DASHBOARD_ANALYTICS,
    ],
    logic: "OR",
  },
  {
    label: "B2B Financial Health",
    path: "/b2b/financial-health",
    icon: <HealthAndSafetyIcon />,
    permission: [Permissions.B2B_COMPANY_READ, Permissions.B2B_FINANCE_READ],
    logic: "OR",
  },
  {
    label: "B2B Companies",
    path: "/b2b/companies",
    icon: <BusinessIcon />,
    permission: Permissions.B2B_COMPANY_READ,
  },
  {
    label: "B2B Distributors",
    path: "/suppliers",
    icon: <LocalShippingIcon />,
    permission: Permissions.SUPPLIER_READ,
  },
  {
    label: "B2B Orders Center",
    path: "/b2b/orders",
    icon: <ShoppingCartIcon />,
    permission: Permissions.B2B_ORDER_READ,
  },
  {
    label: "Quotation Center",
    path: "/b2b/quotes",
    icon: <RequestQuoteIcon />,
    permission: Permissions.B2B_QUOTE_READ,
  },
  {
    label: "Pre-Order Center",
    path: "/preorders/desk",
    icon: <CampaignIcon />,
    permission: Permissions.PREORDER_READ,
  },
  {
    label: "Payment Slips",
    path: "/b2b/payment-slips",
    icon: <ReceiptLongIcon />,
    permission: Permissions.B2B_FINANCE_READ,
  },
  {
    label: "Consignment Control",
    path: "/b2b/consignment",
    icon: <InventoryIcon />,
    permission: Permissions.B2B_CONSIGNMENT_READ,
  },
  {
    label: "Stock Transfers",
    path: "/b2b/stock-transfers",
    icon: <SyncAltIcon />,
    permission: Permissions.B2B_STOCK_TRANSFER_READ,
  },
  {
    label: "B2B Billing Center",
    path: "/b2b/billing",
    icon: <ReceiptLongIcon />,
    permission: Permissions.B2B_BILLING_READ,
  },
  {
    label: "Global Adjustments",
    path: "/b2b/adjustments",
    icon: <AccountBalanceWalletIcon />,
    permission: Permissions.B2B_FINANCE_READ,
  },
  {
    label: "Payment Reminders & Collections",
    path: "/b2b/dunning",
    icon: <WarningAmberIcon />,
    permission: Permissions.B2B_FINANCE_READ,
  },
  {
    label: "Financial Reports",
    path: "/b2b/finance-report",
    icon: <MonetizationOnIcon />,
    permission: Permissions.B2B_FINANCE_READ,
  },
  {
    label: "Logistics Center",
    path: "/b2b/logistics",
    icon: <LocalShippingIcon />,
    permission: Permissions.B2B_LOGISTICS_MANAGE,
  },
  {
    label: "Price Tiers (Templates)",
    path: "/b2b/price-list-templates",
    icon: <LocalOfferIcon />,
    permission: Permissions.B2B_PRICELIST_READ,
  },
  {
    label: "OBO Orders Center",
    path: "/b2b/obo",
    icon: <PointOfSaleIcon />,
    permission: Permissions.B2B_ORDER_OBO,
  },
];

export const Sidebar = ({
  mobileOpen,
  handleDrawerToggle,
  isCollapsed,
  setIsCollapsed,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const [openSections, setOpenSections] = useState(() => {
    const saved = localStorage.getItem(LocalStorageKeys.SIDEBAR_OPEN_SECTIONS);
    return saved
      ? JSON.parse(saved)
      : {
          GENERAL: true,
          B2C: true,
          B2B: true,
          SUPPORT: true,
        };
  });

  useEffect(() => {
    localStorage.setItem(
      LocalStorageKeys.SIDEBAR_OPEN_SECTIONS,
      JSON.stringify(openSections),
    );
  }, [openSections]);

  const toggleSection = (section) => {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const currentDrawerWidth = isCollapsed
    ? COLLAPSED_DRAWER_WIDTH
    : FULL_DRAWER_WIDTH;

  const isActive = (item) => {
    if (item.path === AppRoutes.ROOT)
      return location.pathname === AppRoutes.ROOT;
    const pathMatch = location.pathname === item.path;
    if (item.search) {
      return pathMatch && location.search.includes(item.search);
    }
    return pathMatch && (!location.search || location.search === "");
  };

  const handleNavigate = (path) => {
    navigate(path);
    if (mobileOpen) handleDrawerToggle();
  };
  const renderItems = (items, color = "#00e5ff", collapsed) =>
    items
      .filter((item) =>
        hasPermission(item.permission, { logic: item.logic || "AND" }),
      )
      .map((item) => (
        <ListItem
          key={`${item.path}-${item.label}`}
          disablePadding
          sx={{ mb: 0.5 }}
        >
          <Tooltip title={collapsed ? item.label : ""} placement="right" arrow>
            <ListItemButton
              onClick={() => handleNavigate(item.path + (item.search || ""))}
              selected={isActive(item)}
              sx={{
                borderRadius: 2,
                mx: 1,
                justifyContent: collapsed ? "center" : "initial",
                px: collapsed ? 0 : 2,
                minHeight: 38,
                py: 0.5,
                "&.Mui-selected": {
                  bgcolor: `${color}14`,
                  borderLeft: collapsed ? "none" : "3px solid",
                  borderColor: color,
                  "& .MuiListItemIcon-root": { color: color },
                  "& .MuiListItemText-primary": {
                    color: color,
                    fontWeight: 700,
                  },
                },
                "&:hover": { bgcolor: "rgba(255, 255, 255, 0.04)" },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: collapsed ? 0 : 36,
                  color: "text.secondary",
                  justifyContent: "center",
                }}
              >
                {item.icon}
              </ListItemIcon>
              {!collapsed && (
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ fontSize: "0.85rem", noWrap: true }}
                />
              )}
            </ListItemButton>
          </Tooltip>
        </ListItem>
      ));

  const renderSection = (title, items, key, color = "#94a3b8", collapsed) => {
    const visibleItems = items.filter((item) =>
      hasPermission(item.permission, { logic: item.logic || "AND" }),
    );
    if (visibleItems.length === 0) return null;
    const isOpen = openSections[key] || false;

    return (
      <Box key={key} sx={{ mb: 1 }}>
        {!collapsed && (
          <ListItemButton
            onClick={() => toggleSection(key)}
            sx={{
              px: 3,
              py: 0.75,
              "&:hover": { bgcolor: "transparent", opacity: 0.8 },
            }}
          >
            <Typography
              variant="overline"
              sx={{
                flexGrow: 1,
                color: color,
                opacity: 0.7,
                fontSize: "0.65rem",
                letterSpacing: 1.5,
                fontWeight: 800,
              }}
            >
              {title}
            </Typography>
            {isOpen ? (
              <ExpandLess sx={{ fontSize: 16, color: color, opacity: 0.5 }} />
            ) : (
              <ExpandMore sx={{ fontSize: 16, color: color, opacity: 0.5 }} />
            )}
          </ListItemButton>
        )}
        <Collapse in={collapsed || isOpen} timeout="auto" unmountOnExit>
          <List dense sx={{ px: collapsed ? 0.5 : 1, pb: collapsed ? 0 : 1 }}>
            {renderItems(visibleItems, color, collapsed)}
          </List>
        </Collapse>
      </Box>
    );
  };

  const renderDrawerContent = (forceExpand = false) => {
    const collapsed = forceExpand ? false : isCollapsed;
    return (
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            p: 2.5,
            display: "flex",
            alignItems: "center",
            justifyContent: collapsed ? "center" : "flex-start",
            gap: 1.5,
            minHeight: 64,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontFamily: "'Audiowide', cursive",
              color: "primary.main",
              fontSize: collapsed ? "1.5rem" : "1.1rem",
              lineHeight: 1,
            }}
          >
            {collapsed ? "J" : "JIGENPASS"}
          </Typography>
          {!collapsed && (
            <Typography
              variant="caption"
              sx={{
                bgcolor: "rgba(0, 229, 255, 0.1)",
                color: "primary.main",
                px: 1,
                py: 0.25,
                borderRadius: 1,
                fontSize: "0.65rem",
                fontWeight: 700,
              }}
            >
              BO
            </Typography>
          )}
        </Box>

        <Divider sx={{ borderColor: "divider" }} />

        <Divider sx={{ borderColor: "divider" }} />

        <Box
          sx={{ flexGrow: 1, overflowY: "auto", overflowX: "hidden", py: 1 }}
        >
          {renderSection(
            "CORE PLATFORM",
            generalItems,
            "GENERAL",
            "#94a3b8",
            collapsed,
          )}
          {renderSection(
            "RETAIL HUB (B2C)",
            b2cItems,
            "B2C",
            "#00e5ff",
            collapsed,
          )}
          {renderSection(
            "ENTERPRISE HUB (B2B)",
            b2bItems,
            "B2B",
            "#fbbf24",
            collapsed,
          )}
          {renderSection(
            "AFTER-SALES & SUPPORT",
            supportItems,
            "SUPPORT",
            "#f87171",
            collapsed,
          )}
        </Box>

        {!forceExpand && (
          <Box
            sx={{
              p: 1,
              display: "flex",
              justifyContent: collapsed ? "center" : "flex-end",
            }}
          >
            <IconButton
              onClick={() => setIsCollapsed(!isCollapsed)}
              size="small"
              sx={{
                bgcolor: "rgba(255, 255, 255, 0.05)",
                "&:hover": { bgcolor: "rgba(255, 255, 255, 0.1)" },
              }}
            >
              {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box
      component="nav"
      sx={{
        width: { sm: currentDrawerWidth },
        flexShrink: { sm: 0 },
        transition: (theme) =>
          theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
      }}
    >
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", sm: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: FULL_DRAWER_WIDTH,
            bgcolor: "background.paper",
          },
        }}
      >
        {renderDrawerContent(true)}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", sm: "block" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: currentDrawerWidth,
            bgcolor: "background.paper",
            borderRight: "1px solid",
            borderColor: "divider",
            transition: (theme) =>
              theme.transitions.create("width", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
              }),
            overflowX: "hidden",
          },
        }}
        open
      >
        {renderDrawerContent(false)}
      </Drawer>
    </Box>
  );
};
